import { useState, type MutableRefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { createCustomRecipe, uploadCustomRecipeImageFile } from '@/lib/apiService';
import { Ingredient } from '@/components/recipes/IngredientList';
import { RecipeFormState } from './useRecipeForm';
import { showErrorToast } from '@/utils/toast-utils';
import { FoodCategoryPortion } from '@/types/food-category';
import { RecipeServingSize } from '@/types/custom-recipe';
import { getPublicRecipeNameConflictMessage } from '@/utils/recipe-conflict';

const GRAM_SERVING_PATTERN = /^(g|gram|grams)$/i;

const isGramServing = (value: unknown): boolean => {
  const normalized = String(value ?? '').trim();
  if (!normalized) return false;
  return GRAM_SERVING_PATTERN.test(normalized);
};

const resolvePortionGrams = (portion: FoodCategoryPortion): number | null => {
  const servingIdRaw = portion.servingId ?? portion.id ?? portion.name;
  const gramsRaw = Number(portion.gramsOverride ?? portion.weightG);
  if (Number.isFinite(gramsRaw) && gramsRaw > 0) {
    return Math.round(gramsRaw * 100) / 100;
  }

  if (isGramServing(servingIdRaw)) {
    return 1;
  }

  return null;
};

const resolvePortionMl = (portion: FoodCategoryPortion): number | null => {
  const mlRaw = Number(portion.volumeMl);
  if (Number.isFinite(mlRaw) && mlRaw > 0) {
    return Math.round(mlRaw * 100) / 100;
  }
  return null;
};

interface UseRecipeCreationParams {
  formState: RecipeFormState & {
    portionSizeId: string;
    portionSizes: FoodCategoryPortion[];
  };
  ingredients: Ingredient[];
  draftId: string;
  clientId: string;
  day: string;
  mealName: string;
  returnTo?: string;
  manualAllergies: string[];
  manualIntoleranceTags: string[];
  /** Selected image file for presigned upload (omit recipeId for new recipes). */
  recipeImageFileRef: MutableRefObject<File | null>;
}

export const useRecipeCreation = ({
  formState,
  ingredients,
  draftId,
  clientId,
  day,
  mealName,
  returnTo = '',
  manualAllergies,
  manualIntoleranceTags,
  recipeImageFileRef,
}: UseRecipeCreationParams) => {
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const [duplicateRecipeMessage, setDuplicateRecipeMessage] = useState<string | null>(null);

  const buildServingSizes = (): RecipeServingSize[] => {
    const selectedServingId = typeof formState.portionSizeId === 'string' && formState.portionSizeId.trim().length > 0
      ? formState.portionSizeId.trim().toLowerCase()
      : null;
    const defaultServingGrams = typeof formState.defaultServingGrams === 'number' && formState.defaultServingGrams > 0
      ? Math.round(formState.defaultServingGrams * 100) / 100
      : null;
    const selectedServingDefaultGrams = selectedServingId && isGramServing(selectedServingId)
      ? 1
      : defaultServingGrams;

    const seen = new Set<string>();
    const sizes: RecipeServingSize[] = [];
    for (const portion of formState.portionSizes ?? []) {
      const servingIdRaw = portion.servingId ?? portion.id ?? portion.name;
      const servingId = typeof servingIdRaw === 'string' ? servingIdRaw.trim().toLowerCase() : '';
      if (!servingId || seen.has(servingId)) continue;
      const grams = resolvePortionGrams(portion);
      if (!Number.isFinite(grams) || grams <= 0) continue;
      seen.add(servingId);
      sizes.push({
        servingId,
        grams,
        ml: resolvePortionMl(portion) ?? undefined,
        densityGPerMl: Number.isFinite(Number(portion.densityGPerMl)) && Number(portion.densityGPerMl) > 0
          ? Math.round(Number(portion.densityGPerMl) * 10000) / 10000
          : undefined,
        quantity: Number.isFinite(Number(portion.quantity)) && Number(portion.quantity) > 0
          ? Math.round(Number(portion.quantity) * 100) / 100
          : 1,
        label: typeof portion.name === 'string' && portion.name.trim().length > 0 ? portion.name.trim() : undefined,
        isDefault: selectedServingId === servingId,
      });
    }

    if (selectedServingId) {
      const defaultIndex = sizes.findIndex((entry) => entry.servingId === selectedServingId);
      if (defaultIndex >= 0) {
        sizes[defaultIndex] = {
          ...sizes[defaultIndex],
          isDefault: true,
        };
      } else if (selectedServingDefaultGrams != null) {
        sizes.push({
          servingId: selectedServingId,
          grams: selectedServingDefaultGrams,
          isDefault: true,
        });
      }
    }

    return sizes.length > 0 ? sizes : [];
  };

  const handleCreateRecipe = async () => {
    // Validation
    if (!formState.recipeName.trim()) {
      showErrorToast(new Error("Please enter a recipe name."), "Recipe Name Required");
      return;
    }

    if (ingredients.length === 0) {
      showErrorToast(new Error("Please add at least one ingredient."), "Ingredients Required");
      return;
    }

    if (formState.mealTypes.length === 0) {
      showErrorToast(new Error('Please select at least one meal type.'), 'Meal Type Required');
      return;
    }

    setIsCreating(true);
    setDuplicateRecipeMessage(null);
    
    try {
      // Prepare serving-size data for API
      const servingSizes = buildServingSizes();
      const selectedServingId = typeof formState.portionSizeId === 'string' && formState.portionSizeId.trim().length > 0
        ? formState.portionSizeId.trim().toLowerCase()
        : '';

      if (servingSizes.length === 0) {
        showErrorToast(
          new Error('Add at least one portion size before creating the recipe.'),
          'Portion Sizes Required'
        );
        return;
      }

      if (!selectedServingId) {
        showErrorToast(new Error('Please select a default portion size.'), 'Default Portion Required');
        return;
      }

      const invalidPortions = formState.portionSizes.filter((portion, index) => {
        const servingIdRaw = portion.servingId ?? portion.id ?? portion.name ?? `portion-${index}`;
        const servingId = String(servingIdRaw).trim().toLowerCase();
        const grams = resolvePortionGrams(portion);
        const ml = resolvePortionMl(portion);
        const density = Number(portion.densityGPerMl);
        return servingId.length > 0 && ml != null && (
          !Number.isFinite(grams) || grams <= 0 || !Number.isFinite(density) || density <= 0
        );
      });
      if (invalidPortions.length > 0) {
        showErrorToast(
          new Error('ML values require density and a valid grams value for the same portion size.'),
          'Portion Sizes Invalid'
        );
        return;
      }

      const defaultServing = selectedServingId
        ? servingSizes.find((entry) => entry.servingId === selectedServingId)
        : servingSizes.find((entry) => entry.isDefault);

      if (selectedServingId && !defaultServing) {
        showErrorToast(
          new Error('Default serving size must have a valid grams value.'),
          'Default Serving Weight Required'
        );
        return;
      }

      let imageUrl: string | undefined;
      if (recipeImageFileRef.current) {
        try {
          imageUrl = await uploadCustomRecipeImageFile(recipeImageFileRef.current);
        } catch (e) {
          showErrorToast(e instanceof Error ? e : new Error('Could not upload image'), 'Error');
          setIsCreating(false);
          return;
        }
      }

      const safeServings =
        typeof formState.servings === 'number' && Number.isFinite(formState.servings) && formState.servings > 0
          ? formState.servings
          : 1;

      const recipeData = {
        recipeName: formState.recipeName,
        aliases: formState.aliases
          .split(',')
          .map((alias) => alias.trim())
          .filter(Boolean),
        description: formState.description,
        ingredients: ingredients.map(ing => ({
          ingredientId: ing.ingredientId,
          name: ing.name,
          quantity: ing.amount,
          unit: ing.unit,
          amountGrams: ing.amountGrams,
          amountMl: ing.amountMl,
          unitWeightGrams: ing.unitWeightGrams,
          unitWeightMl: ing.unitWeightMl,
          yieldFactor: ing.yieldFactor,
          retentionFactor: ing.retentionFactor,
          allergens: ing.allergens,
          allergenTagIds: ing.allergenTagIds,
          intoleranceTags: ing.intoleranceTags,
          intoleranceTagIds: ing.intoleranceTagIds,
        })),
        servings: safeServings,
        nutritionServings: 1,
        instructions: formState.instructions.split('\n').filter(step => step.trim()),
        cookingTips: formState.cookingTips,
        allergens: formState.allergies,
        manualAllergens: manualAllergies,
        dietLabels: formState.dietLabels,
        intoleranceTags: formState.intoleranceTags,
        manualIntoleranceTags,
        cuisineTypes: formState.cuisineTypes,
        mealTypes: formState.mealTypes,
        dishTypes: formState.dishTypes.length > 0 ? formState.dishTypes : ['main-dish'],
        cookingMethod: formState.cookingMethod || undefined,
        prepTimeMinutes: typeof formState.prepTimeMinutes === 'number' ? formState.prepTimeMinutes : 10,
        cookTimeMinutes: typeof formState.cookTimeMinutes === 'number' ? formState.cookTimeMinutes : 20,
        totalTimeMinutes: formState.totalTimeMinutes || 30,
        isPublic: formState.shareWithOthers,
        defaultServingId: selectedServingId || undefined,
        defaultServingGrams: defaultServing?.grams,
        ...(servingSizes.length > 0 ? { servingSizes } : {}),
        ...(imageUrl ? { imageUrl } : {}),
      };

      // Call the API to create custom recipe
      const response = await createCustomRecipe(recipeData);
      
      if (response.success) {
        const safeReturnTo = returnTo.startsWith('/') ? returnTo : '';
        const hasMealPlanContext = Boolean(safeReturnTo || (draftId && clientId));

        // When creating from a meal plan, return there instead of recipe details.
        if (hasMealPlanContext) {
          if (safeReturnTo) {
            navigate(safeReturnTo);
          } else {
            const params = new URLSearchParams();
            if (day) params.set('day', day);
            if (mealName) params.set('mealName', mealName);
            const query = params.toString();
            navigate(
              query
                ? `/manual-meal-plan/${draftId}/client/${clientId}?${query}`
                : `/manual-meal-plan/${draftId}/client/${clientId}`,
            );
          }
          return;
        }

        const recipeId = response.data?.id || response.recipeId;
        if (recipeId) {
          navigate(`/recipes/${recipeId}`);
        } else {
          navigate('/recipes');
        }
      } else {
        throw new Error(response.message || 'Failed to create recipe');
      }
    } catch (error) {
      console.error('Error creating recipe:', error);
      const conflictMessage = getPublicRecipeNameConflictMessage(error);
      if (conflictMessage) {
        setDuplicateRecipeMessage(conflictMessage);
        return;
      }
      showErrorToast(error, "Error");
    } finally {
      setIsCreating(false);
    }
  };

  return {
    isCreating,
    handleCreateRecipe,
    duplicateRecipeMessage,
    clearDuplicateRecipeMessage: () => setDuplicateRecipeMessage(null),
  };
};
